export const TILE = 1;
export const SIM_HZ = 60;
export const DT = 1 / SIM_HZ;
export const PLAYER_RADIUS = 0.34;
export const ENEMY_RADIUS = 0.38;

export type Mode = "title" | "overworld" | "home" | "hollow" | "death" | "pause";

export const Tile = {
  Grass: 1,
  Dirt: 2,
  Path: 3,
  Soil: 4,
  Crop: 5,
  Water: 6,
  Tree: 7,
  Fence: 8,
  Wall: 9,
  Floor: 10,
  Door: 11,
  Stone: 12,
  Ruin: 13,
  Well: 14,
  Tall: 15,
  Wood: 16,
  Roof: 17,
  Hollow: 18,
  Exit: 19,
  Vein: 20,
  Acid: 21,
} as const;
export type TileId = (typeof Tile)[keyof typeof Tile];

export type WeatherKind = "clear" | "overcast" | "rain" | "storm" | "fog";
export type Season = "thaw" | "highsun" | "ember" | "ashfall";

export type ItemId =
  | "ration"
  | "seed_root"
  | "seed_bitter"
  | "herb"
  | "scrap"
  | "cinder"
  | "rope"
  | "lantern_oil"
  | "bandage"
  | "note"
  | "artifact"
  | "strange_root"
  | "survey_plate";

export interface ItemStack {
  id: ItemId;
  n: number;
  meta?: Record<string, string | number>;
}

export interface Vec {
  x: number;
  y: number;
}

export interface Player {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: Vec;
  hp: number;
  hpMax: number;
  stamina: number;
  staminaMax: number;
  hunger: number;
  injury: number;
  dashT: number;
  dashCd: number;
  strikeT: number;
  strikePhase: "idle" | "wind" | "active" | "recover";
  strikeLanded: boolean;
  lantern: boolean;
  oil: number;
  invuln: number;
  overworldX: number;
  overworldY: number;
}

export interface Crop {
  x: number;
  y: number;
  kind: "root" | "bitter" | "strange";
  stage: number;
  water: number;
  contamination: number;
  plantedAt: number;
}

export interface HomeState {
  dirt: number;
  blood: number;
  furniture: string[];
  bench: Array<ItemStack | null>;
  chest: ItemStack[];
  trophies: string[];
  visits: number;
}

export interface NpcMemory {
  playerHurt: number;
  playerDepth: number;
  gifts: number;
  lastSeenHour: number;
  missing?: boolean;
}

export interface Npc {
  id: string;
  name: string;
  occupation: string;
  faction: FactionId;
  x: number;
  y: number;
  homeX: number;
  homeY: number;
  color: number;
  schedule: ScheduleBlock[];
  memory: NpcMemory;
  mood: number;
  alive: boolean;
}

export interface ScheduleBlock {
  from: number;
  to: number;
  x: number;
  y: number;
  verb: string;
}

export type FactionId =
  | "wardens"
  | "survey"
  | "ember"
  | "salvage"
  | "deep"
  | "deniers"
  | "fence"
  | "none";

export interface FactionState {
  id: FactionId;
  stance: number;
  control: number;
}

export type EnemyKind =
  | "lurcher"
  | "listener"
  | "skitter"
  | "watcher"
  | "vein"
  | "coward";

export interface Enemy {
  id: number;
  kind: EnemyKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  hpMax: number;
  state: "idle" | "suspect" | "hunt" | "flee" | "wind" | "recover" | "dead";
  facing: Vec;
  alert: number;
  wind: number;
  stun: number;
  radius: number;
}

export interface Decal {
  x: number;
  y: number;
  kind: "blood" | "scorch" | "ash" | "foot" | "cache";
  age: number;
  life: number;
  hue: number;
  layer: "overworld" | "hollow" | "home";
  depth?: number;
}

export interface DeathCache {
  id: string;
  x: number;
  y: number;
  layer: "overworld" | "hollow";
  depth?: number;
  items: ItemStack[];
  createdAt: number;
  recovered: boolean;
}

export interface WorldEvent {
  t: number;
  text: string;
  kind: "story" | "system" | "faction";
}

/** A fact that already happened. See `sim/events.ts`. */
export type SignalKind =
  | "descend"
  | "depth_record"
  | "extract"
  | "death"
  | "artifact_stored"
  | "storm";

export interface Signal {
  kind: SignalKind;
  t: number;
  mag: number;
  depth: number;
  layer: "overworld" | "hollow" | "home";
  at: Vec;
  tag?: string;
}

export interface WatchState {
  deaths: number;
  extracts: number;
  depth: number;
  deepest: number;
  weather: WeatherKind;
  shelved: number;
}

export interface HollowRun {
  seed: number;
  depth: number;
  w: number;
  h: number;
  tiles: Uint8Array;
  start: Vec;
  down?: Vec;
  enemies: Enemy[];
  loot: Array<{ x: number; y: number; item: ItemStack; taken: boolean }>;
  landmarks: Array<{ x: number; y: number; id: string; title: string; read: boolean }>;
  enteredAt: number;
  maxDepthThisRun: number;
}

export interface Overworld {
  w: number;
  h: number;
  tiles: Uint8Array;
  well: Vec;
  homeDoor: Vec;
  homeOrigin: Vec;
  store: Vec;
  wake: Vec;
  pois: Record<string, Vec>;
  crops: Crop[];
}

export interface Knowledge {
  wellSeen: boolean;
  hollowEntered: boolean;
  deepest: number;
  juneMissing: boolean;
  plates: number;
  extractedOnce: boolean;
}

export const SAVE_VERSION = 2;

export interface WorldState {
  seed: number;
  version: number;
  mode: Mode;
  prevMode: Mode;
  timeSec: number;
  weather: WeatherKind;
  weatherT: number;
  season: Season;
  player: Player;
  bag: ItemStack[];
  safeBag: ItemStack[];
  overworld: Overworld;
  home: HomeState;
  hollow: HollowRun | null;
  npcs: Npc[];
  factions: FactionState[];
  memory: Decal[];
  caches: DeathCache[];
  log: WorldEvent[];
  knowledge: Knowledge;
  msg: string;
  msgT: number;
  shake: number;
  deaths: number;
  extracts: number;
  signals?: Signal[];
  restlessness?: number;
  watch?: WatchState;
}

export type LiveWorld = WorldState & {
  signals: Signal[];
  restlessness: number;
  watch: WatchState;
};

export function idx(x: number, y: number, w: number): number {
  return (y | 0) * w + (x | 0);
}

export function inBounds(x: number, y: number, w: number, h: number): boolean {
  return x >= 0 && y >= 0 && x < w && y < h;
}
